import { z } from 'zod';
import { KolSearchStatusEnum, type ChatDateFilter } from '../enums/index.js';
import type { Citation as ChatCitation } from './chat.citation.js';
import type { CanonicalWidgetData } from './dashboard-data.js';
import type { HcpProfileWidget, HcpProfileWidgetType } from './hcp-profile-config.js';
import type { KolProfile } from './kol.profile.js';
import { recommendationReasonSchema } from './kol.profile.js';
import { type ICoords } from './location.js';

export const KolSearchStatus = z.enum(KolSearchStatusEnum);
export type KolSearchStatus = z.infer<typeof KolSearchStatus>;

export enum KolListTypeEnum {
  Recommendations = 'recommendations',
}

export const KolListType = z.enum(KolListTypeEnum);
export type KolListType = z.infer<typeof KolListType>;

export const KolSearchResultModel = z.object({
  id: z.coerce.number(),
  isKol: z.boolean(),
  affiliations: z.array(
    z.object({
      // id: z.coerce.number(),
      name: z.string(),
      city: z.string().nullish(),
      state: z.string().nullish(),
      street: z.string().nullish(),
      country: z.string().nullish(),
      role: z.string().nullish(),
      zip: z.string().nullish(),
    })
  ),
  name: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  qualifications: z.array(z.string()),
  specialties: z.array(z.string()),

  city: z.string().nullish(),
  state: z.string().nullish(),
  streetAddress: z.string().nullish(),
  country: z.string().nullish(),
  zipCode: z.string().nullish(),

  distance: z.number().nullish(),
  locationConfidence: z.number().nullish(),
  locationCoords: z
    .object({
      latitude: z.number(),
      longitude: z.number(),
    })
    .nullable()
    .optional(),
  locationName: z.string().nullable().optional(),

  recommendations: z.array(recommendationReasonSchema).nullable().optional(),
});
export type KolSearchResult = z.infer<typeof KolSearchResultModel>;

export type KolSearchRecommendation = KolSearchResult;

export type KolSearchForm = {
  kolId: number;
  name: string;
  location?: {
    id: string;
    latitude: number;
    longitude: number;
    name: string;
  };
};

export type InteractionsTimeRangeMetadata = {
  numDocumentedInterests: number;
  numContactCenterInteractions: number;
  numFbmsInteractions: number;
  numMedicalInsights: number;
  withMe: number;
  total: number;
};

export type InteractionsMetadata = {
  allTime: InteractionsTimeRangeMetadata;
  lookback: InteractionsTimeRangeMetadata;
  lookbackPeriod: ChatDateFilter;
} & Partial<InteractionsTimeRangeMetadata>;

export type KolProfileMessage = {
  id: number;
  affiliations: KolProfile.Affiliation[];
  interactions: InteractionsMetadata;
  keyDiscussionQuestions: string[];
  keyInsights: string[];
  keyFocusAreas?: KolProfile.ScientificFocusArea[];
  name: string;

  numClinicalTrials: number;
  numLeadAuthorPublications: number;
  numPublications: number;
  numCongressAbstracts: number;

  numScientificFocusAreas: number;
  numClinicalFocusAreas: number;
  numDigitalFocusAreas: number;

  profileLink: string | null;
  qualifications: string[];
  recommendations: KolProfile.RecommendationReason[];
  specialty: string;
  title: string | null;
  events: GroupedKolEvent[];
  upcomingEvents?: KolProfile.Event[];

  contact: {
    address: string | null;
    email: string | null;
    phone: string | null;
  };
  locationCoords: ICoords | null;

  availabilityInsights?: AvailabilityInsights;

  profileConfig?: {
    sections: Array<{
      key: string;
      name: string;
      order: number;
      enabled: boolean;
      isAI?: boolean;
      widgets: HcpProfileWidget[];
    }>;
  };

  widgetData?: Record<
    string,
    {
      widgets: Array<{
        id: string;
        type: HcpProfileWidgetType;
        data: CanonicalWidgetData;
      }>;
    }
  >;

  customSections?: Record<
    string,
    {
      content: string;
    }
  >;
};

export type GroupedKolEvent = {
  id: number;
  name: string;
  startDate: Date;
  endDate: Date | null;
  roles: {
    position: string | null;
    talkTitle: string | null;
  }[];
};

export type KolSearchRecommendations = {
  status: 'uninitialized' | 'loading' | 'loaded' | 'skipped';
  items: KolSearchRecommendation[];
  total: number;
};

export type KolSearch = {
  id: number;
  identifier: string;
  status: KolSearchStatusEnum;
  formData: KolSearchForm;
  recommendations: KolSearchRecommendations;
  results: KolSearchResult[];
  selectedProfile: KolProfileMessage | null;
  followupQuestions: string[] | null;
  citations: ChatCitation[];
};

export const AvailabilityInsightsSchema = z.object({
  totalInteractions: z.number(),
  disclaimer: z.string(),
  insights: z.record(
    z.string(),
    z.object({
      count: z.number(),
      pct: z.number(),
      rank: z.enum(['HIGH', 'MEDIUM', 'LOW', 'ZERO']),
    })
  ),
});

export type AvailabilityInsights = z.infer<typeof AvailabilityInsightsSchema>;
